import type { DecisionVerdictOutcome, DecisionVerdictResponse } from '@wix/ai-scheduling-assistant-common-lib/utils';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { clampLedgerText, plainLedgerText, type LedgerText, type WebLedgerDecision, type WebLedgerOption } from './goal-ledger';

/**
 * ANSWERING a decision, from the web — the rules, with no React in them.
 *
 * The Goal Ledger has always been able to show you a question and never to answer it, and the reason it
 * is a whole module rather than three handlers on a row is that almost everything here is a way to get
 * an irreversible act subtly wrong. Four of them, each of which looks like it works:
 *
 * **1. `planVersion` is HELD, not read.** A verdict must echo the plan version the row was RENDERED
 * with; that echo is the entire staleness mechanism (`CastVerdict` compares the two for exact equality,
 * absence included). The ledger refetches on every task write, so the `decision` prop under an open row
 * moves — and a submit that reads `decision.planVersion` at the moment of the tap compares the plan
 * against itself and always passes, which deletes the check while looking identical. {@link holdPlanVersion}
 * pins the value to the row's first sight of that decision id and keeps it.
 *
 * **2. Absent is a value.** A decision raised before the goal was ever revised carries no version, and
 * the key must then be OMITTED from the body — `'0'` and `''` are both mismatches against an empty
 * `Optional<Long>` and get a perfectly good answer refused as stale. {@link buildVerdictBody} omits.
 *
 * **3. The `verdictId` is a replay pin, so a retry must re-send the SAME one.** It travels as the
 * service's `fromTurnId` and is what makes a lost response safe to press again: the re-read finds our own
 * id already on the row and reports `ALREADY_RECORDED` instead of recording a second answer. A fresh id
 * per press would vote twice. {@link pinVerdictId} mints one per distinct ANSWER — the same answer keeps
 * its id forever, a different answer gets a new one, because those are genuinely two taps.
 *
 * **4. The outcome is the payload, not the HTTP status.** Every classified result comes back 200,
 * refusals included, and `ok` says whether the answer is on file. A client branching on `res.ok` reports
 * "answered" for `EXPIRED`. {@link castDecisionVerdict} branches on nothing but `outcome`, and treats a
 * non-200 — 401, 403, 5xx, offline — as the ONE case where we cannot say whether the verdict landed:
 * `failed`, question stays open, retry with the same pin.
 *
 * The copy each result maps to is {@link decisionOutcomeEffect}, and it is deliberately the only place
 * that decides whether the reader may press again.
 */

/** A transport failure — the one result that is not an outcome, because it is not classified at all. */
export const DECISION_SEND_FAILED = 'SEND_FAILED';

/** What a cast produced: the server's own classification, or the un-classifiable send failure. */
export type DecisionCastResult =
  | { kind: 'outcome'; response: DecisionVerdictResponse }
  | { kind: typeof DECISION_SEND_FAILED };

/** Which control shape a decision gets. `unanswerable` is a row we can show and cannot answer. */
export type DecisionControl = 'approve' | 'confirm' | 'choose' | 'answer' | 'unanswerable';

/** The answer a reader committed to, before it is a request body. */
export interface DecisionAnswer {
  resolution: 'ACCEPTED' | 'REFUSED';
  /** An `Option.id` — never a label. Only ever set for an accepted CHOOSE. */
  choice?: string;
  /** The typed text: an accepted ANSWER's answer, or the note a refusal of ANY kind carries. */
  answer?: string;
}

/** The `planVersion` a row was rendered with, pinned to the decision it belongs to. */
export interface PlanVersionHold {
  decisionId: string;
  planVersion?: string;
}

/** The idempotency key for one answer, and the answer it belongs to. */
export interface VerdictPin {
  signature: string;
  verdictId: string;
}

/** Cap on an ANSWER's text — this surface's call, well under the proto's 65536 (spec §12, D7). */
export const DECISION_ANSWER_LIMIT = 4000;
/** Remaining characters at which the counter appears. Below it, a counter is noise. */
export const DECISION_ANSWER_COUNTER_AT = 400;

/**
 * Which control this decision gets.
 *
 * **An unrecognised kind renders as APPROVE, never as unanswerable.** `resolution` is required on every
 * verdict and orthogonal to kind, so ACCEPTED/REFUSED is legal for a kind this build has never heard of
 * — and the two-button shape is the only one that needs no kind-specific data to draw.
 *
 * The single unanswerable case is a CHOOSE with no options. `≥2 distinct options` is a server invariant,
 * and the ledger route omits the field entirely rather than sending `[]`, so an absent one here means the
 * row is corrupt. An empty picker is not a smaller picker; it is a question with no answers, and
 * synthesising "Approve/Decline" for it would send `approve` to a decision whose vocabulary is neither.
 */
export function decisionControl(decision: WebLedgerDecision): DecisionControl {
  const kind = decision.kind?.trim().toUpperCase();
  if (kind === 'ANSWER') return 'answer';
  if (kind === 'CONFIRM') return 'confirm';
  if (kind === 'CHOOSE') return decisionOptions(decision).length > 0 ? 'choose' : 'unanswerable';
  return 'approve';
}

/** The alternatives, in the order the agent authored them. Never re-sorted, never alphabetised. */
export function decisionOptions(decision: WebLedgerDecision): WebLedgerOption[] {
  return (decision.options ?? []).filter((option) => !!option?.id && !!option?.label?.trim());
}

/** Only an OPEN decision may be answered. Everything else is history, whatever it is doing in `open`. */
export function isAnswerable(decision: WebLedgerDecision): boolean {
  return decision.status?.trim().toUpperCase() === 'OPEN';
}

/**
 * The `planVersion` this row answers against.
 *
 * Returns the previous hold whenever it is for the same decision, so a ledger refetch that brings a NEW
 * `planVersion` under an open row does not quietly re-target the answer at a plan the reader never saw.
 * That is the difference between a staleness check and a formality.
 */
export function holdPlanVersion(previous: PlanVersionHold | null, decision: WebLedgerDecision): PlanVersionHold {
  if (previous && previous.decisionId === decision.id) return previous;
  const planVersion = decision.planVersion?.trim();
  return { decisionId: decision.id, ...(planVersion ? { planVersion } : {}) };
}

/** Two answers are the SAME tap iff every field the service records is the same. */
export function answerSignature(answer: DecisionAnswer): string {
  // The separator is written as an ESCAPE, not as a raw control byte. A literal NUL in the source makes
  // git classify this file as binary — the commit that added it shows `Bin 0 -> 16121 bytes` and no diff
  // at all, on the one file in this feature where every line is a rule about an irreversible act.
  return [answer.resolution, answer.choice ?? '', answer.answer ?? ''].join('\u0000');
}

/**
 * The replay pin for an answer: the same one on every retry, a new one for a different answer.
 *
 * A retry that re-mints is not a retry — it is a second vote — and the whole reason the service takes a
 * client-minted id is so that a response lost on the way back can be pressed again safely.
 */
export function pinVerdictId(previous: VerdictPin | null, signature: string, mint: () => string): VerdictPin {
  if (previous && previous.signature === signature) return previous;
  return { signature, verdictId: mint() };
}

/**
 * Where a pin is remembered between page loads — the key format both phones already write.
 *
 * `symphony.decision.verdictId.<decisionId>` → `<signature>|<uuid>`, byte-for-byte Android's
 * `StorageKeys.DECISION_VERDICT_ID_PREFIX` and iOS's `StorageKeys.decisionVerdictId(decisionId:)`.
 */
export const DECISION_VERDICT_PIN_PREFIX = 'symphony.decision.verdictId.';

/** Best-effort `localStorage`. Absent in SSR and refused in a locked-down browser; neither is fatal. */
function pinStore(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

/**
 * The pin this browser last sent for `decisionId`, if any.
 *
 * **A tab reload is this surface's process death.** The pin only matters on a RETRY, and the retry that
 * matters is the one after a send whose response never came back — which is exactly the moment somebody
 * reloads the page and presses the button again. A pin held only in a React ref is a FRESH pin after
 * that, and a fresh pin records the same answer twice. Split on the LAST separator: a signature carries
 * separators of its own.
 */
export function readVerdictPin(decisionId: string): VerdictPin | null {
  const raw = pinStore()?.getItem(DECISION_VERDICT_PIN_PREFIX + decisionId);
  if (!raw) return null;
  const cut = raw.lastIndexOf('|');
  if (cut <= 0 || cut === raw.length - 1) return null;
  return { signature: raw.slice(0, cut), verdictId: raw.slice(cut + 1) };
}

/** Remember the pin BEFORE the request goes out, so a reload mid-flight still finds it. */
export function writeVerdictPin(decisionId: string, pin: VerdictPin): void {
  try {
    pinStore()?.setItem(DECISION_VERDICT_PIN_PREFIX + decisionId, `${pin.signature}|${pin.verdictId}`);
  } catch {
    // A full or disabled store costs us the cold-start retry, not the answer. Never throw over it.
  }
}

/** Forget it once the answer is on file — a further press would be a genuinely new answer. */
export function forgetVerdictPin(decisionId: string): void {
  try {
    pinStore()?.removeItem(DECISION_VERDICT_PIN_PREFIX + decisionId);
  } catch {
    // Same.
  }
}

/** A UUID, from the platform's own generator where there is one. */
export function mintVerdictId(): string {
  const cryptoApi = typeof globalThis !== 'undefined' ? (globalThis.crypto as Crypto | undefined) : undefined;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * The request body — with the two omissions that decide whether the answer is accepted at all.
 *
 * `planVersion` is omitted when the HELD value was absent (see the module note). `choice` and `answer`
 * are sent only for the kind that has them and only on an ACCEPTED verdict: the service would normalise
 * a stray one away, but sending a field a kind does not have is how the next kind gets added wrong.
 */
export function buildVerdictBody(args: {
  decisionId: string;
  verdictId: string;
  answer: DecisionAnswer;
  hold: PlanVersionHold;
}): Record<string, string> {
  const { decisionId, verdictId, answer, hold } = args;
  return {
    decisionId,
    verdictId,
    resolution: answer.resolution,
    // A CHOICE is only ever an acceptance: refusing picks nothing, and sending the option the reader
    // turned down would file their "no" as a "yes" to it.
    ...(answer.resolution === 'ACCEPTED' && answer.choice ? { choice: answer.choice } : {}),
    // WORDS ride either resolution. `Verdict.answer` is orthogonal to `resolution` on the wire (65,536
    // chars on both), and `planDecisionVerdict` in common-lib — the one implementation this route and
    // the native bridge share — already passes it through whatever the resolution says. Stripping it
    // here on a refusal was this client alone deciding a "no" may not say why, which is exactly the
    // thing the refuse note exists to give back: a reader who turns down every option can now say what
    // they would rather have, and the agent gets more than the word "no".
    ...(answer.answer ? { answer: answer.answer } : {}),
    ...(hold.planVersion ? { planVersion: hold.planVersion } : {}),
  };
}

/** What a result means for the row: what it says, whether the reader may press again, and what to re-read. */
export interface DecisionOutcomeEffect {
  /** The i18n key the row shows. Never a server message — those are English log lines. */
  messageKey: string;
  /** Whether the controls stay usable. `false` means pressing again cannot help. */
  retryable: boolean;
  /** Whether to re-read the ledger, because the authoritative row now says something else. */
  reread: boolean;
  /** `danger` is the two the reader can act on; the rest are statements of fact, not alarms. */
  tone: 'danger' | 'neutral';
}

/**
 * Every result, mapped to something TRUE.
 *
 * The three that must not be got wrong, and each of which a simpler mapping gets wrong:
 *
 * - **`EXPIRED` is not a refusal.** Nobody said no; nobody said anything. It reads as the lapse it is.
 * - **`WITHDRAWN` is not an answer the user gave.** The agent took the question back.
 * - **`ALREADY_SETTLED` is not "you answered".** Something else settled it, and the ledger re-read will
 *   say what — so the placeholder is the neutral `settled.plain`, which claims no author and no verdict.
 *
 * `CLOSED` is the honest "we could not establish which of the three" — it promises the reload rather than
 * naming a cause, which is exactly what we know at that instant.
 *
 * The `ok` outcomes never reach here: a recorded answer is rendered as its settled sentence, not as a
 * message about itself.
 */
export function decisionOutcomeEffect(outcome: DecisionVerdictOutcome | typeof DECISION_SEND_FAILED): DecisionOutcomeEffect {
  switch (outcome) {
    case 'STALE_PLAN':
      return { messageKey: 'goals.decision.stale', retryable: false, reread: true, tone: 'neutral' };
    case 'ALREADY_SETTLED':
      return { messageKey: 'goals.ledger.settled.plain', retryable: false, reread: true, tone: 'neutral' };
    case 'EXPIRED':
      return { messageKey: 'goals.ledger.expired', retryable: false, reread: true, tone: 'neutral' };
    case 'WITHDRAWN':
      return { messageKey: 'goals.ledger.withdrawn', retryable: false, reread: true, tone: 'neutral' };
    case 'CLOSED':
      return { messageKey: 'goals.decision.closed', retryable: false, reread: true, tone: 'neutral' };
    case 'NOT_ANSWERABLE':
      return { messageKey: 'goals.decision.unanswerable', retryable: false, reread: false, tone: 'neutral' };
    case 'INVALID_CHOICE':
    case 'ANSWER_REQUIRED':
    case 'RESOLUTION_REQUIRED':
    case 'INVALID_VERDICT_ID':
      return { messageKey: 'goals.decision.rejected', retryable: true, reread: false, tone: 'danger' };
    case DECISION_SEND_FAILED:
      // The request never got a classified answer, so nothing is known to have changed and there is
      // nothing to re-read. The row stays, enabled, saying it could not send.
      return { messageKey: 'goals.decision.failed', retryable: true, reread: false, tone: 'danger' };
    default:
      // An outcome a future server adds that this build has never seen. It arrived on a classified
      // response with `ok: false`, so ONE thing about it is established: the answer is not on file.
      // "That answer wasn't accepted" says exactly that and no more, and leaves the question open and
      // pressable. It deliberately does NOT read as `failed` ("couldn't send") — we did get an answer,
      // and it does not re-read, because an outcome we cannot name has not established that the row on
      // screen is wrong. Both phones already did it this way; this is the surface that moved.
      return { messageKey: 'goals.decision.rejected', retryable: true, reread: false, tone: 'danger' };
  }
}

/** Whether a result put the reader's answer on file. Read off the response, never inferred from a name. */
export function isAnswerRecorded(result: DecisionCastResult): boolean {
  return result.kind === 'outcome' && result.response.ok === true;
}

/**
 * The settled sentence for a row that was just answered.
 *
 * Built from the decision the RESPONSE returned wherever there is one — that is the entity as it now
 * stands, and a locally-assembled one would be this client's guess at what the service recorded. The
 * choice comes back as an option **id** (the response is the raw entity, not the ledger's normalised
 * shape), so it is resolved to its label against the options the row was rendered with; `opt-2` on a
 * user's screen is the same failure as a raw enum.
 *
 * With no returned decision — `ok` was true but the entity did not come with it — the rendered row plus
 * the answer that was just recorded is the fallback. It says less than the entity would and nothing that
 * is untrue, and the ledger re-read replaces it a moment later either way.
 */
export function settledDecisionView(
  rendered: WebLedgerDecision,
  answer: DecisionAnswer,
  returned: unknown,
): WebLedgerDecision {
  const entity = (returned ?? {}) as {
    status?: string;
    kind?: string;
    outcome?: { resolution?: string; choice?: string; answer?: string };
    verdicts?: { resolution?: string; choice?: string; answer?: string; principal?: { kind?: string } }[];
  };
  const verdicts = entity.verdicts ?? [];
  const last = verdicts.length ? verdicts[verdicts.length - 1] : undefined;
  const resolution = entity.outcome?.resolution ?? last?.resolution ?? answer.resolution;
  const choiceId = entity.outcome?.choice ?? last?.choice ?? answer.choice;
  const answerText = entity.outcome?.answer ?? last?.answer ?? answer.answer;
  const principal = last?.principal?.kind;
  return {
    ...rendered,
    status: entity.status ?? 'SETTLED',
    kind: entity.kind ?? rendered.kind,
    outcome: {
      resolution,
      ...(choiceId ? { choice: optionLabel(rendered, choiceId) } : {}),
      ...(answerText ? { answer: answerText } : {}),
      ...(principal ? { principal } : {}),
    },
  };
}

/** An option id in WORDS. An id matching nothing falls back to itself — an unknown answer is still it. */
export function optionLabel(decision: WebLedgerDecision, id: string): string {
  return decisionOptions(decision).find((option) => option.id === id)?.label ?? id;
}

/** The agent's own context for a question: markdown STRIPPED and capped, exactly as the ledger's prose is. */
export function decisionDetailText(decision: WebLedgerDecision, limit?: number): LedgerText | undefined {
  const full = plainLedgerText(decision.detail);
  if (!full) return undefined;
  const clamped = clampLedgerText(full, limit);
  return { text: clamped.text, full, truncated: clamped.truncated };
}

/**
 * POST the verdict.
 *
 * **A non-200 is never an outcome.** The route answers 200 for everything it could classify — refusals
 * included — so a status outside that is 401/403/5xx or the network, and every one of them leaves us
 * unable to say whether the verdict landed. That is `SEND_FAILED`, which renders as "couldn't send, try
 * again" and keeps the question open. Rendering it as anything else would be claiming an answer we have
 * no evidence of.
 */
export async function castDecisionVerdict(body: Record<string, string>): Promise<DecisionCastResult> {
  try {
    const res = await apiFetch(`${API_BASE}/api/decision-verdict`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.status !== 200) return { kind: DECISION_SEND_FAILED };
    const parsed = (await res.json()) as Partial<DecisionVerdictResponse> | null;
    if (!parsed || typeof parsed.outcome !== 'string') return { kind: DECISION_SEND_FAILED };
    return { kind: 'outcome', response: parsed as DecisionVerdictResponse };
  } catch {
    return { kind: DECISION_SEND_FAILED };
  }
}
